// Chess entry point: settings → i18n → canvas + layout → scenes → menu.
// Glue only; construction detail lives in scenes/.
import '@bn-games/ui-shell/styles.css';
import './styles/moveList.css';
import './styles/exportImport.css';
import { MenuRouter, createMainMenuScreen, openModal, setupMenuScale } from '@bn-games/ui-shell';
import { setMuted } from '@bn-games/audio-core';
import { BN_AUTH_CHANGE, fetchMe } from '@bn-games/auth-client';
import { readRoomHash } from '@bn-games/room-client';
import { initI18n, setLocale, t } from './i18n/I18n.js';
import { registerServiceWorker } from './pwa.js';
import { LayoutManager } from './layout/LayoutManager.js';
import type { LayoutConfig } from './layout/types.js';
import { SettingsManager } from './settings/SettingsManager.js';
import { THEMES } from './settings/themes.js';
import type { Settings } from './settings/types.js';
import { GameScene } from './scenes/GameScene.js';
import { PauseHost } from './scenes/PauseHost.js';
import { buildMenuContext } from './scenes/MenuContextImpl.js';
import { OnlineMenuHost } from './scenes/OnlineMenuHost.js';
import { buildInGameSettingsRows } from './ui/inGameSettings.js';
import { mountSocialDock } from './ui/socialDock.js';
import { resolveLobbyServerUrl } from './scenes/onlineHostConfig.js';
import { createPlayMenuScreen } from './ui/screens/PlayMenu.js';
import { createOnlineCreateScreen } from './ui/screens/OnlineCreateScreen.js';
import { createOnlineJoinScreen } from './ui/screens/OnlineJoinScreen.js';
import { createRulesScreen } from './ui/screens/Rules.js';
import { createSettingsScreen } from './ui/screens/Settings.js';
import { createAboutScreen } from './ui/screens/About.js';
import { createHistoryListScreen } from './ui/screens/HistoryListScreen.js';
import { createImportPositionScreen } from './ui/screens/ImportPositionScreen.js';
import { ReplayScene } from './history/ReplayScene.js';
import { flushOutbox } from './history/serverSync.js';
import type { ChessMenuContext, Scene } from './ui/types.js';
import { CANVAS_SIZE } from './utils/constants.js';
import { APP_VERSION } from './utils/version.js';

function getMenuRoot(): HTMLElement {
  const el = document.getElementById('menu-root');
  if (el === null) throw new Error('#menu-root not found');
  return el;
}

function createCanvas(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.setAttribute('tabindex', '0');
  canvas.setAttribute('role', 'application');
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('Failed to acquire 2D canvas context');
  return { canvas, ctx };
}

// Backing store at display size × DPR; drawing code stays in CANVAS_SIZE units.
function applyCanvasLayout(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, layout: LayoutConfig): void {
  const css = layout.canvasDisplaySize;
  const bitmap = Math.max(1, Math.round(css * layout.devicePixelRatio));
  if (canvas.width !== bitmap) canvas.width = bitmap;
  if (canvas.height !== bitmap) canvas.height = bitmap;
  canvas.style.width = `${css}px`;
  canvas.style.height = `${css}px`;
  const scale = bitmap / CANVAS_SIZE;
  ctx.setTransform(scale, 0, 0, scale, 0, 0);
}

function applyTheme(theme: Settings['theme']): void {
  document.body.setAttribute('data-theme', THEMES[theme].name);
}

async function main(): Promise<void> {
  const settings = new SettingsManager();
  await initI18n(settings.get().language);
  registerServiceWorker();
  setupMenuScale();
  applyTheme(settings.get().theme);
  setMuted(!settings.get().sound);

  const { canvas, ctx } = createCanvas();
  canvas.setAttribute('aria-label', t('a11y.canvasLabel'));
  const layout = new LayoutManager();
  applyCanvasLayout(canvas, ctx, layout.getConfig());
  layout.onChange((cfg) => applyCanvasLayout(canvas, ctx, cfg));

  let current: Scene | null = null;
  const navigate = (scene: Scene): void => {
    if (scene === current) return;
    const leaving = current;
    current = scene;
    socialDock.showScene(scene);
    // The game and a replay both draw on the canvas; every other scene is a DOM menu.
    const onCanvas = scene === 'game' || scene === 'replay';
    document.body.setAttribute('data-mode', onCanvas ? scene : 'menu');
    if (leaving === 'game') {
      pause.close();
      gameScene.unmount();
    }
    if (leaving === 'replay') replay.close();
    if (onCanvas) router.hide();
    else router.show(scene);
  };

  // Pending invites: the full card on the main menu, a pill everywhere else.
  const socialDock = mountSocialDock({
    serverUrl: resolveLobbyServerUrl(),
    isGameInProgress: () => current === 'game' && gameScene.isOnlineGameLive(),
  });

  // Leaving the board by any route. An online table is dropped here rather
  // than in `navigate`, so only a deliberate exit gives up the seat.
  const leaveGame = (): void => {
    online.leaveTable();
    navigate('menu');
  };

  const gameScene = new GameScene({
    canvas, ctx, settings,
    openPause: () => pause.open(),
    exitToMenu: leaveGame,
    onRestartStateChange: () => pause.refreshRestart(),
  });
  // Owns the RoomClient and the single menu→board handoff, so the online
  // screens stay DOM-only and never see a socket.
  const online = new OnlineMenuHost({ gameScene, settings, navigate });

  const onSettingsChanged = (before: Settings, after: Settings): void => {
    if (after.theme !== before.theme) applyTheme(after.theme);
    if (after.sound !== before.sound) setMuted(!after.sound);
    if (after.language !== before.language) {
      void setLocale(after.language).then(() => {
        canvas.setAttribute('aria-label', t('a11y.canvasLabel'));
        router.remountCurrent();
        pause.refresh();
        gameScene.relabel();
      });
    }
  };

  const replay = new ReplayScene({ canvas, ctx, settings, onExit: navigate });
  const openReplay: ChessMenuContext['openReplay'] = (record, returnTo) => {
    if (replay.open(record, returnTo)) {
      navigate('replay');
      return;
    }
    const modal = openModal({
      title: t('historyList.unreadableTitle'),
      body: t('historyList.unreadableBody'),
      actions: [{ label: t('gameOverlay.close'), primary: true, onClick: () => modal.close() }],
    });
  };

  const menuCtx = buildMenuContext({ settings, gameScene, navigate, onSettingsChanged, online, openReplay });
  const pause = new PauseHost({
    settingsRows: () => buildInGameSettingsRows({ ctx: menuCtx, isOnline: () => gameScene.isOnline(), restartMatchOffline: () => gameScene.restart() }),
    restart: () => gameScene.restart(),
    resign: () => gameScene.resign(),
    exitToMenu: leaveGame,
    inGame: () => current === 'game',
    isOnline: () => gameScene.isOnline(),
    restartState: () => gameScene.restartState(),
    drawOffer: () => gameScene.drawOffer(),
  });

  const router = new MenuRouter(getMenuRoot(), menuCtx);
  router.register('menu', () => createMainMenuScreen<ChessMenuContext>({
    version: APP_VERSION,
    onQuickStart: () => menuCtx.startLocalGame('bot'),
  }));
  router.register('play', createPlayMenuScreen);
  router.register('rules', createRulesScreen);
  router.register('settings', createSettingsScreen);
  router.register('about', createAboutScreen);
  router.register('history', createHistoryListScreen);
  // The shared scene list has no "import" entry; this is chess's screen before a game.
  router.register('pre-game', createImportPositionScreen);
  router.register('online-create', createOnlineCreateScreen);
  router.register('online-join', createOnlineJoinScreen);
  navigate('menu');

  // Prime the signed-in state once, so the first online connect can mint a
  // ticket — and retry any online result still waiting to reach the account.
  void fetchMe().then(flushOutbox);
  // Signing in or out changes what the history screen lists, and who the outbox belongs to.
  window.addEventListener(BN_AUTH_CHANGE, () => {
    flushOutbox();
    if (router.getCurrentScene() === 'history') router.remountCurrent();
  });
  // A share or invite link (`#room=CODE`) wins over a saved game. A saved
  // unfinished game means the tab was reloaded mid-game: go straight back.
  const link = readRoomHash();
  const saved = link === null ? online.getActiveSession() : null;
  if (link !== null) void online.resumeRoom(link.roomCode, link.legacyToken);
  else if (saved !== null) void online.resumeRoom(saved.roomCode);
}

void main();
